import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faClose} from '@fortawesome/free-solid-svg-icons/faClose';
import React, {ReactNode, useRef, useState} from 'react';
import {
	CashOffer,
	FinanceOffer,
	LeaseOffer,
	MsrpOffer,
	OFFER_TYPE,
	OFFER_TYPE_NICE_NAME,
	OfferModel,
} from '../../models/offer.model';
import {getCashBackWording} from '../../services/offer.service';
import {isRequiredForFtc} from '../../services/settings.service';
import {InTransitChip} from '../common/InTransitChip';

const Offer = ({offer, handleOfferSelected}: {offer: OfferModel, handleOfferSelected: (id: string) => void}) => {
	const {
		car_title,
		img,
		category,
		terms,
		custom_text,
	} = offer;

	const [showTerms, setShowTerms] = useState(false);
	const [hasClickListener, setHasClickListener] = useState(false);
	const termsRef = useRef<HTMLDivElement>(null);
	const isRequiredByFtc = isRequiredForFtc(offer);

	const handleCloseTerms = (event: MouseEvent): void => {
		if (!termsRef.current?.contains(event.target as Node)) {
			setShowTerms(false);
		}
	};

	const secondaryLeaseDetails = (offer: LeaseOffer): ReactNode => {
		if (isRequiredByFtc) {
			return (
				<>
					{offer.lease_down_payment && <div>${offer.lease_down_payment} down payment</div>}
					{offer.lease_due_at_signing && <div>${offer.lease_due_at_signing} due at signing</div>}
					{offer.security_deposit && <div>${offer.security_deposit} security deposit</div>}
				</>
			);
		}

		if (offer.lease_down_payment) {
			return <div>${offer.lease_down_payment} down payment</div>;
		}
		if (offer.lease_due_at_signing) {
			return <div>${offer.lease_due_at_signing} due at signing</div>;
		}
		return null;
	};

	const secondaryFinanceDetails = (offer: FinanceOffer): ReactNode => {
		if (!isRequiredByFtc || !offer.finance_down_payment || !offer.finance_amount_per_month) {
			return null;
		}
		return (
			<>
				<div>${offer.finance_down_payment} down payment</div>
				<div>${offer.finance_amount_per_month} / mo.</div>
			</>
		);
	};

	const cashBackDetails = (offer: CashOffer): ReactNode => {
		const cashBackWording = getCashBackWording(offer);
		return <div>${offer.cash_amount} {cashBackWording}</div>;
	};

	const renderLeaseDetails = (leaseOffer: LeaseOffer): ReactNode => (
		<div className="text-sm">
			{leaseOffer.lease_amount_per_month && leaseOffer.lease_months && (
				<div>${leaseOffer.lease_amount_per_month} mo. | {leaseOffer.lease_months} mos.</div>
			)}
			{secondaryLeaseDetails(leaseOffer)}
		</div>
	);

	const renderFinanceDetails = (financeOffer: FinanceOffer): ReactNode => (
		<div className="text-sm">
			{financeOffer.finance_apr && financeOffer.finance_months && (
				<div>{financeOffer.finance_apr}% APR for {financeOffer.finance_months} mos.</div>
			)}
			{secondaryFinanceDetails(financeOffer)}
			{custom_text && <div>{custom_text}</div>}
		</div>
	);

	const renderCashDetails = (cashOffer: CashOffer): ReactNode => (
		cashOffer.cash_amount ? (
			<div className="text-sm">
				{cashBackDetails(cashOffer)}
			</div>
		) : null
	);

	const renderMsrpDetails = (msrpOffer: MsrpOffer): ReactNode => (
		msrpOffer.off_amount ? (
			<div className="text-sm">
				<div>${msrpOffer.off_amount} Off MSRP</div>
			</div>
		) : null
	);

	const handleOfferSelection = (offer: OfferModel): void => {
		setShowTerms(false);
		handleOfferSelected(offer.id);
	};

	const toggleTerms = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>): void => {
		setShowTerms(!showTerms);
		event.stopPropagation();
		if (!showTerms) {
			if (!hasClickListener) {
				document.addEventListener('click', handleCloseTerms, {capture: true});
				setHasClickListener(true);
			}
		} else {
			document.removeEventListener('click', handleCloseTerms, {capture: true});
			setHasClickListener(false);
		}
	};

	return (
		<div className="flex justify-center">
			<div className="relative w-[220px] flex flex-col p-4 border rounded-md">
				<img src={img} alt="offer image"/>
				<InTransitChip isInTransit={offer.is_in_transit} className="self-start mt-2" />

				<div>
					<div className="text-xs pt-[8px]">{OFFER_TYPE_NICE_NAME[category]}</div>
					<div className="font-bold text-lg capitalize leading-tight">{car_title}</div>

					{category === OFFER_TYPE.LEASE && renderLeaseDetails(offer as LeaseOffer)}
					{category === OFFER_TYPE.FINANCE && renderFinanceDetails(offer as FinanceOffer)}
					{category === OFFER_TYPE.CASH && renderCashDetails(offer as CashOffer)}
					{category === OFFER_TYPE.OFF_MSRP && renderMsrpDetails(offer as MsrpOffer)}

					{terms && (
						<div className="relative mt-[4px] w-full">
							<button type="button" className="text-xs cursor-pointer text-gray-500" onClick={toggleTerms}>
								Terms & Conditions
							</button>
							{showTerms &&
								<div ref={termsRef}
									className="absolute left-[-10] top-full mt-[-16px] w-[212px] max-h-[min(50vh,240px)] overflow-y-auto bg-white text-xs rounded-md border border-primary p-2 text-gray-700 shadow-lg">
									<div onClick={toggleTerms}
										className="absolute right-[0px] top-[0px] py-[6px] px-[12px] text-lg cursor-pointer">
										<FontAwesomeIcon icon={faClose}/>
									</div>
									<div className="font-medium mb-[12px]">Terms & Conditions</div>
									{terms}
								</div>
							}
						</div>
					)}
				</div>

				<button	className="self-center border border-primary bg-primary rounded-full text-white font-bold mt-[20px] py-2 px-4 hover:bg-white hover:text-primary cursor-pointer"
						onClick={() => handleOfferSelection(offer)}>
					Claim Offer
				</button>

			</div>
		</div>
	);
};

export default Offer;
